import { useCallback, useState } from 'react';

import { useSearchParts } from '@frontend/api';

import ExpandableFilterList from '../components/ExpandableFilterList';
import PartMatchCell from '../components/PartMatchCell';
import SearchResultList from '../components/SearchResultList';
import Searching from '../components/Searching';
import { UserGroupFilter } from '../components/UserGroupFilter';
import { TABS } from '../constants';
import { useSearchDataContext } from '../context';
import {
  buildFilterSeeMore,
  buildFilterUpdate,
} from '../utils/buildFilterUpdate';

const ALWAYS_VISIBLE_FIELDS = [
  'partName',
  'partNumber',
  'revision',
  'accountName',
  'contactEmail',
  'contactFullName',
  'number',
  'process',
  'material',
  'finishes',
];

const PartsResultList = () => {
  const { userHasTyped, query, activeTab } = useSearchDataContext();

  const [paginateFrom, setPaginateFrom] = useState(query.parts.paginateFrom);
  const [filters, setFilters] = useState(query.parts.filters);

  const { data: parts } = useSearchParts(
    {
      ...query.parts,
      paginateFrom,
      filters,
    },
    {
      queryOptions: {
        keepPreviousData: true,
        enabled: activeTab === TABS.PARTS,
      },
    },
  );

  const onProcessChange = useCallback(
    (process) => buildFilterUpdate(setFilters, 'process')(process),
    [],
  );

  const onProcessSeeMore = useCallback(
    () => buildFilterSeeMore(setFilters, 'process')(),
    [],
  );

  const onMaterialChange = useCallback(
    (material) => buildFilterUpdate(setFilters, 'materials')(material),
    [],
  );

  const onMaterialSeeMore = useCallback(
    () => buildFilterSeeMore(setFilters, 'materials')(),
    [],
  );

  const onFinishChange = useCallback(
    (finish) => buildFilterUpdate(setFilters, 'finishes')(finish),
    [],
  );

  const onFinishSeeMore = useCallback(
    () => buildFilterSeeMore(setFilters, 'finishes')(),
    [],
  );

  if (userHasTyped) {
    return <Searching />;
  }

  return (
    <SearchResultList
      data={parts}
      tabKey={TABS.PARTS}
      showComponentMatches
      labelPlural="Parts"
      labelSingular="Part"
      matchCell={(result) => <PartMatchCell partResult={result} />}
      highlightConfig={{
        alwaysVisibleFields: ALWAYS_VISIBLE_FIELDS,
        children: {
          comments: [{ field: 'content', key: 'comments', label: 'Comments' }],
          partFiles: [
            { field: 'filename', key: 'partFiles', label: 'Part Files' },
          ],
          wordSpans: [
            { field: 'text', key: 'wordSpans', label: 'PDF Text Matches' },
          ],
        },
        allowLinks: ['comments', 'partFiles', 'wordSpans'],
      }}
      filters={
        <>
          <ExpandableFilterList
            agg={parts?.aggs.processesAgg.processes}
            label="Process"
            selectedKeys={filters.process.values}
            onClick={onProcessChange}
            onSeeMore={onProcessSeeMore}
          />
          <ExpandableFilterList
            agg={parts?.aggs.materialsAgg.materials}
            label="Materials"
            selectedKeys={filters.materials.values}
            onClick={onMaterialChange}
            onSeeMore={onMaterialSeeMore}
          />
          <ExpandableFilterList
            agg={parts?.aggs.finishesAgg.finishes}
            label="Finishes"
            selectedKeys={filters.finishes.values}
            onClick={onFinishChange}
            onSeeMore={onFinishSeeMore}
          />
          <UserGroupFilter
            agg={parts?.aggs.groupIdsAgg.groupIds}
            selectedGroupIds={filters.groupIds?.values ?? []}
            setFilters={setFilters}
          />
        </>
      }
      paginationInfo={{
        paginateFrom,
        paginateSize: query.parts.paginateSize,
      }}
      onPaginate={(pagination) => setPaginateFrom(pagination.paginateFrom)}
    />
  );
};

export default PartsResultList;
